import React, { useEffect } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import { FR_LEGAL_DOCS, FR_LEGAL_LINKS } from "@/lib/legalFr";
import { usePageMeta } from "@/lib/seo";
import { openCookieSettings } from "@/components/CookieConsent";

const Mark = ({ text }) => {
  const parts = text.split(/(\[[^\]]+\])/g);
  return parts.map((x, i) => (x.startsWith("[") ? <mark key={i} className="bg-amber-100 text-amber-900 rounded px-1">{x}</mark> : x));
};

export default function FrLegalPage({ docKey }) {
  const params = useParams();
  const key = docKey || params.doc;
  const doc = FR_LEGAL_DOCS[key];
  usePageMeta(doc ? `${doc.title} | Trustline Services France` : null, doc ? `${doc.title} — TRUSTLINE SERVICES (France).` : "", { canonicalPath: `/fr/${key}` });
  useEffect(() => { window.scrollTo(0, 0); }, [key]);
  useEffect(() => {
    if (!doc?.hidden) return undefined;
    const m = Object.assign(document.createElement("meta"), { name: "robots", content: "noindex, nofollow" });
    document.head.appendChild(m);
    return () => m.remove();
  }, [doc]);
  if (!doc) return <Navigate to="/fr" replace />;
  return (
    <div className="max-w-3xl mx-auto px-5 md:px-8 py-12 md:py-16" lang="fr" data-testid={`fr-legal-${key}`}>
      <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#0EA5E9]">Trustline Services France</p>
      <h1 className="text-3xl md:text-4xl text-[#0F172A] mt-2">{doc.title}</h1>
      <div className="mt-5 flex gap-2 items-start rounded-xl bg-amber-50 ring-1 ring-amber-200 p-4 text-sm text-amber-900" data-testid="fr-legal-draft-banner">
        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
        <span>Document en cours de validation juridique. Les mentions surlignées sont à compléter ou vérifier avant publication.</span>
      </div>
      {doc.sections.map((s) => (
        <section key={s.h} className="mt-8">
          <h2 className="text-xl md:text-2xl text-[#1B3A6B]">{s.h}</h2>
          {(s.p || []).map((t) => <p key={t} className="mt-2 text-sm text-slate-700 leading-relaxed"><Mark text={t} /></p>)}
          {s.list && <ul className="mt-2 list-disc pl-5 space-y-1 text-sm text-slate-700">{s.list.map((t) => <li key={t}><Mark text={t} /></li>)}</ul>}
          {(s.p2 || []).map((t) => <p key={t} className="mt-2 text-sm text-slate-700 leading-relaxed"><Mark text={t} /></p>)}
          {s.link && <Link to={s.link[0]} className="mt-2 inline-block text-sm font-semibold text-[#0EA5E9] hover:underline">{s.link[1]} →</Link>}
        </section>
      ))}
      {doc.cookieButton && (
        <button type="button" onClick={openCookieSettings} className="mt-8 inline-flex items-center px-5 h-11 rounded-full text-sm font-semibold text-white bg-[#1B3A6B] hover:bg-[#102547]" data-testid="fr-cookies-open-settings">
          Modifier mes choix de cookies
        </button>
      )}
      <nav className="mt-12 pt-6 border-t border-slate-200 flex flex-wrap gap-x-4 gap-y-2 text-sm">
        {FR_LEGAL_LINKS.map(([to, l]) => <Link key={to} to={to} className="text-slate-600 hover:text-[#0EA5E9]">{l}</Link>)}
      </nav>
    </div>
  );
}
